import "./redux/axiosSetup";
import { useEffect, useRef, useState } from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { createRoot } from "react-dom/client";

import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  useOutlet,
  useNavigate,
  redirect,
} from "react-router-dom";

import Login from "./containers/pages/login";
import Home from "./containers/pages/home";
import AppHeader from "./components/navigation/AppHeader.jsx";
import ProductosPage from "./containers/pages/ProductosPage.jsx";
import CrearProductoPage from "./containers/pages/CrearProductoPage.jsx";
import ProductoDetallePage from "./containers/pages/ProductoDetallePage.jsx";
import ServiciosPage from "./containers/pages/ServiciosPage.jsx";
import CrearServicioPage from "./containers/pages/CrearServicioPage.jsx";
import ServicioDetallePage from "./containers/pages/ServicioDetallePage.jsx";
import ProveedoresPage from "./containers/pages/ProveedoresPage.jsx";
import CrearProveedorPage from "./containers/pages/CrearProveedorPage.jsx";
import ProveedorDetallePage from "./containers/pages/ProveedorDetallePage.jsx";
import ClientesPage from "./containers/pages/ClientesPage.jsx";
import CrearClientePage from "./containers/pages/CrearClientePage.jsx";
import ClienteDetallePage from "./containers/pages/ClienteDetallePage.jsx";
import ComprasPage from "./containers/pages/ComprasPage.jsx";
import CrearCompraPage from "./containers/pages/CrearCompraPage.jsx";
import CompraDetallePage from "./containers/pages/CompraDetallePage.jsx";
import VentasPage from "./containers/pages/VentasPage.jsx";
import CrearVentaPage from "./containers/pages/CrearVentaPage.jsx";
import VentaDetallePage from "./containers/pages/VentaDetallePage.jsx";
import Cajero from "./containers/pages/cajero.jsx";
import CortesPage from "./containers/pages/CortesPage.jsx";
import CorteDetallePage from "./containers/pages/CorteDetallePage.jsx";
import GastosPage from "./containers/pages/GastosPage.jsx";
import CrearGastoPage from "./containers/pages/crearGastoPage.jsx";
import GastoDetallePage from "./containers/pages/GastoDetallePage.jsx";
import PromocionesPage from "./containers/pages/PromocionesPage.jsx";
import CrearPromocionPage from "./containers/pages/CrearPromocionPage.jsx";
import PromocionDetallePage from "./containers/pages/PromocionDetallePage.jsx";
import ReparacionesPage from "./containers/pages/ReparacionesPage.jsx";
import CrearReparacionPage from "./containers/pages/CrearReparacionPage.jsx";
import ReparacionDetallePage from "./containers/pages/ReparacionDetallePage.jsx";
import EncargosPage from "./containers/pages/EncargosPage.jsx";
import CrearEncargoPage from "./containers/pages/CrearEncargoPage.jsx";
import EncargoDetallePage from "./containers/pages/EncargoDetallePage.jsx";
import FacturacionPage from "./containers/pages/FacturacionPage.jsx";
import UsuariosPage from "./containers/pages/UsuariosPage.jsx";
import CrearUsuarioPage from "./containers/pages/CrearUsuarioPage.jsx";
import UsuarioDetallePage from "./containers/pages/UsuarioDetallePage.jsx";
import PeriodoDetallePage from "./containers/pages/PeriodoDetallePage.jsx";
import PerfilPage from "./containers/pages/PerfilPage.jsx";
import ConfiguracionPage from "./containers/pages/ConfiguracionPage.jsx";
import EstadisticasPage from "./containers/pages/EstadisticasPage.jsx";

import { ConfigProvider } from "antd";
import { showHerramientasEffect } from "./components/utils/antWaveEffect";
import store from "./store";
import { isTokenExpired, tokenExpiresInMs } from "./utils/tokenUtils";
import { sessionManager } from "./redux/sessionManager";
import ModalReautenticar from "./components/modals/ModalReautenticar";
import GlobalToastHost from "./components/toasts/GlobalToastHost.jsx";
import { Provider, useDispatch } from "react-redux";
import { PermisosProvider } from "./context/PermisosContext";
import RequireModulo from "./components/guards/RequireModulo";
import LoginHeader from "./components/navigation/LoginHeader.jsx";
import "./assets/css/bootstrap.css";
import "./styles.css";

// Modulos de negocio agregan aqui su ruta conforme se construyen
// (ventas, clientes, etc.). Cajero es pantalla completa,
// sin sidebar/header (ver isFullScreenApp abajo), igual que Cocina en Terraza.
const routes = [
  { path: "/login", element: <Login /> },
  { path: "/app/home", element: <Home /> },
  { path: "/app/productos", element: <ProductosPage />, modulo: "productos" },
  { path: "/app/productos/crear", element: <CrearProductoPage />, modulo: "productos" },
  { path: "/app/productos/:idProducto", element: <ProductoDetallePage />, modulo: "productos" },
  { path: "/app/productos/:idProducto/editar", element: <CrearProductoPage />, modulo: "productos" },
  { path: "/app/servicios", element: <ServiciosPage />, modulo: "servicios" },
  { path: "/app/servicios/crear", element: <CrearServicioPage />, modulo: "servicios" },
  { path: "/app/servicios/:idServicio", element: <ServicioDetallePage />, modulo: "servicios" },
  { path: "/app/servicios/:idServicio/editar", element: <CrearServicioPage />, modulo: "servicios" },
  { path: "/app/proveedores", element: <ProveedoresPage />, modulo: "proveedores" },
  { path: "/app/proveedores/crear", element: <CrearProveedorPage />, modulo: "proveedores" },
  { path: "/app/proveedores/:idProveedor", element: <ProveedorDetallePage />, modulo: "proveedores" },
  { path: "/app/proveedores/:idProveedor/editar", element: <CrearProveedorPage />, modulo: "proveedores" },
  { path: "/app/clientes", element: <ClientesPage />, modulo: "clientes" },
  { path: "/app/clientes/crear", element: <CrearClientePage />, modulo: "clientes" },
  { path: "/app/clientes/:idCliente", element: <ClienteDetallePage />, modulo: "clientes" },
  { path: "/app/clientes/:idCliente/editar", element: <CrearClientePage />, modulo: "clientes" },
  { path: "/app/compras", element: <ComprasPage />, modulo: "compras" },
  { path: "/app/compras/crear", element: <CrearCompraPage />, modulo: "compras" },
  { path: "/app/compras/:idCompra", element: <CompraDetallePage />, modulo: "compras" },
  { path: "/app/ventas", element: <VentasPage />, modulo: "ventas" },
  { path: "/app/ventas/crear", element: <CrearVentaPage />, modulo: "ventas" },
  { path: "/app/ventas/:idVenta", element: <VentaDetallePage />, modulo: "ventas" },
  { path: "/app/cajero", element: <Cajero />, modulo: "cajero" },
  { path: "/app/cortes", element: <CortesPage />, modulo: "cortes" },
  { path: "/app/cortes/:idCorte", element: <CorteDetallePage />, modulo: "cortes" },
  { path: "/app/gastos", element: <GastosPage />, modulo: "gastos" },
  { path: "/app/gastos/crear", element: <CrearGastoPage />, modulo: "gastos" },
  { path: "/app/gastos/:idGasto", element: <GastoDetallePage />, modulo: "gastos" },
  { path: "/app/promociones", element: <PromocionesPage />, modulo: "promociones" },
  { path: "/app/promociones/crear", element: <CrearPromocionPage />, modulo: "promociones" },
  { path: "/app/promociones/:idPromocion", element: <PromocionDetallePage />, modulo: "promociones" },
  { path: "/app/promociones/:idPromocion/editar", element: <CrearPromocionPage />, modulo: "promociones" },
  { path: "/app/reparaciones", element: <ReparacionesPage />, modulo: "reparaciones" },
  { path: "/app/reparaciones/crear", element: <CrearReparacionPage />, modulo: "reparaciones" },
  { path: "/app/reparaciones/:idReparacion", element: <ReparacionDetallePage />, modulo: "reparaciones" },
  { path: "/app/encargos", element: <EncargosPage />, modulo: "encargos" },
  { path: "/app/encargos/crear", element: <CrearEncargoPage />, modulo: "encargos" },
  { path: "/app/encargos/:idEncargo", element: <EncargoDetallePage />, modulo: "encargos" },
  { path: "/app/facturacion", element: <FacturacionPage />, modulo: "facturacion" },
  { path: "/app/facturacion/:idPeriodo", element: <PeriodoDetallePage />, modulo: "facturacion" },
  { path: "/app/usuarios", element: <UsuariosPage />, modulo: "usuarios" },
  { path: "/app/usuarios/crear", element: <CrearUsuarioPage />, modulo: "usuarios" },
  { path: "/app/usuarios/:idUser", element: <UsuarioDetallePage />, modulo: "usuarios" },
  { path: "/app/perfil", element: <PerfilPage /> },
  { path: "/app/configuracion", element: <ConfiguracionPage />, modulo: "configuracion" },
  { path: "/app/estadisticas", element: <EstadisticasPage />, modulo: "estadisticas" },
];

const requireAuth = ({ request }) => {
  const url = new URL(request.url);
  const path = url.pathname.replace(/\/+$/, "") || "/";

  if (path.startsWith("/app")) {
    const token = localStorage.getItem("token");
    if (!token || isTokenExpired(token)) {
      if (token) localStorage.removeItem("token");
      throw redirect("/login");
    }
    return null;
  }

  return null;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <AppShell />,
    loader: requireAuth,
    shouldRevalidate: () => true,
    children: [
      { index: true, loader: () => redirect("/app/home") },
      ...routes.map((route) => ({
        path: route.path,
        element: route.modulo
          ? <RequireModulo modulo={route.modulo}>{route.element}</RequireModulo>
          : route.element,
      })),
    ],
  },
]);

function AppShell() {
  useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const nodeRef = useRef(null);
  const currentOutlet = useOutlet();
  const pathname = location.pathname;
  const normalpath = pathname.replace(/\/+$/, "") || "/";

  const isAuthPage = normalpath === "/login";
  const isFullScreenApp = normalpath === "/app/cajero" || normalpath.startsWith("/app/cajero/");
  const showShell = !isAuthPage && !isFullScreenApp;

  const [showReauthModal, setShowReauthModal] = useState(false);

  // Guard en cada navegacion: token vigente entra, caducado/ausente va a login.
  useEffect(() => {
    if (isAuthPage) return;
    const t = localStorage.getItem("token");
    if (!t || isTokenExpired(t)) {
      localStorage.removeItem("token");
      navigate("/login", { replace: true });
    }
  }, [normalpath]);

  useEffect(() => {
    const handleSessionExpired = () => setShowReauthModal(true);
    window.addEventListener("session-expired", handleSessionExpired);
    return () => window.removeEventListener("session-expired", handleSessionExpired);
  }, []);

  useEffect(() => {
    const setupTimer = () => {
      const t = localStorage.getItem("token");
      if (!t || isTokenExpired(t)) return undefined;
      const ms = tokenExpiresInMs(t);
      if (ms <= 0) return undefined;
      return setTimeout(() => {
        if (!sessionManager.isRefreshing) {
          sessionManager.setRefreshing(true);
          window.dispatchEvent(new Event("session-expired"));
        }
      }, ms);
    };

    let timerId = setupTimer();
    const resetTimer = () => {
      if (timerId) clearTimeout(timerId);
      timerId = setupTimer();
    };

    window.addEventListener("permisos-refresh", resetTimer);
    return () => {
      if (timerId) clearTimeout(timerId);
      window.removeEventListener("permisos-refresh", resetTimer);
    };
  }, []);

  if (isAuthPage) {
    return (
      <>
        <ModalReautenticar open={showReauthModal} onClose={() => setShowReauthModal(false)} />
        <LoginHeader />
        <div className="login-shell" style={{ marginTop: 44, height: "calc(100vh - 44px)" }}>
          {currentOutlet}
        </div>
      </>
    );
  }

  const topOffset = isFullScreenApp ? 0 : 44;
  const leftOffset = isFullScreenApp ? 0 : 56;

  return (
    <>
      <ModalReautenticar open={showReauthModal} onClose={() => setShowReauthModal(false)} />
      {showShell && <AppHeader />}
      <div
        className="content-electron"
        style={{
          marginTop: topOffset,
          minHeight: `calc(100vh - ${topOffset}px)`,
          marginLeft: leftOffset,
        }}
      >
        <SwitchTransition>
          <CSSTransition
            key={location.pathname}
            nodeRef={nodeRef}
            timeout={200}
            classNames="page"
          >
            {() => (
              <div ref={nodeRef} className="page">
                {currentOutlet}
              </div>
            )}
          </CSSTransition>
        </SwitchTransition>
      </div>
    </>
  );
}

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <Provider store={store}>
    <PermisosProvider>
      <ConfigProvider wave={{ showEffect: showHerramientasEffect }}>
        <RouterProvider router={router} />
      </ConfigProvider>
      <GlobalToastHost />
    </PermisosProvider>
  </Provider>
);
